import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaUser, FaBuilding, FaPhone, FaBriefcase, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import TurnstileWidget from './TurnstileWidget';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === 'true';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const LicenseRequest = ({
  initialStep = 1,
  initialEmail = '',
  initialEmailVerifiedToken = '',
}) => {
  // Step management: 1 = email, 2 = OTP, 3 = details, 4 = success
  const [step, setStep] = useState(initialStep);

  // Form state
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [emailVerifiedToken, setEmailVerifiedToken] = useState(initialEmailVerifiedToken);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  // UI state
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const otpRefs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileResetKey((v) => v + 1);
  };

  // --- Step 1: Send verification OTP ---
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email) || email.length > 254) {
      return setError('Please enter a valid email address');
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/send-verification-otp`, {
        email: email.trim().toLowerCase(),
      });
      if (res.data?.message) setMessage(res.data.message);
      setCountdown(60);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2: Verify OTP ---
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasted)) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      return setError('Please enter the complete 6-digit code');
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp: otpValue,
      });
      if (res.data?.verified) {
        setEmailVerifiedToken(res.data.email_verified_token);
        setMessage('Email verified successfully!');
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/send-verification-otp`, {
        email: email.trim().toLowerCase(),
      });
      if (res.data?.message) setMessage(res.data.message);
      setOtp(['', '', '', '', '', '']);
      setCountdown(60);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // --- Step 3: Submit license request ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name || !companyName || !phone || !position) {
      return setError('All fields are required');
    }
    if (name.trim().length < 2 || name.trim().length > 120) {
      return setError('Name must be between 2 and 120 characters');
    }
    if (companyName.trim().length < 2 || companyName.trim().length > 120) {
      return setError('Company name must be between 2 and 120 characters');
    }
    if (position.trim().length < 2 || position.trim().length > 120) {
      return setError('Position must be between 2 and 120 characters');
    }
    const phoneDigits = phone.replace(/[^\d]/g, '');
    if (phoneDigits.length < 7 || phoneDigits.length > 20) {
      return setError('Phone number must contain 7 to 20 digits');
    }
    if (TURNSTILE_ENABLED && !turnstileToken) {
      return setError('Please complete the security verification before submitting.');
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/license/request`, {
        name: name.trim(),
        email: email.trim(),
        companyName: companyName.trim(),
        phone: phone.trim(),
        position: position.trim(),
        website,
        turnstileToken,
        email_verified_token: emailVerifiedToken,
      });
      if (res.status === 201) setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request');
      if (TURNSTILE_ENABLED) resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  // --- Shared UI helpers ---
  const stepSubtitle = {
    1: 'Enter your email to receive a verification code',
    2: 'Enter the code sent to your email',
    3: 'Complete your license request details',
    4: 'Your request has been submitted',
  };

  const inputClass =
    'w-full pl-10 pr-4 py-3 border border-white/30 rounded-lg bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-200';
  const btnClass =
    'w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl';

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-purple via-purple-600 to-brand-red flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-96 h-96 bg-white/10 rounded-full animate-pulse delay-500"></div>
        <div className="absolute -bottom-1/2 -left-1/2 w-96 h-96 bg-white/5 rounded-full animate-pulse delay-1500"></div>
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/5 rounded-full animate-pulse delay-700"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-brand-radial rounded-full mb-4 animate-float shadow-2xl"></div>
          <h2 className="text-4xl font-bold text-white mb-2">
            {step === 4 ? 'Request Submitted!' : 'Request License'}
          </h2>
          <p className="text-white/80">{stepSubtitle[step]}</p>
          {step > 1 && step < 4 && (
            <div className="flex justify-center gap-2 mt-4">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    s <= step ? 'w-10 bg-white' : 'w-6 bg-white/30'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Form container */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
          {error && (
            <div className="mb-6 bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-100 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          {message && step !== 4 && (
            <div className="mb-6 bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-100 px-4 py-3 rounded-lg">
              {message}
            </div>
          )}

          {/* ---- STEP 1: Email ---- */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="sr-only">Email address</label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    className={inputClass}
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <button type="submit" disabled={loading} className={btnClass}>
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  'Send Verification Code'
                )}
              </button>
              <div className="text-center">
                <span className="text-white/60">Already have an account? </span>
                <Link to="/login" className="font-medium text-white hover:text-white/80 transition-colors">
                  Sign in here
                </Link>
              </div>
            </form>
          )}

          {/* ---- STEP 2: OTP ---- */}
          {step === 2 && (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div>
                <label className="block text-white/80 text-sm mb-3">Verification Code</label>
                <div className="flex justify-between gap-2" onPaste={handleOtpPaste}>
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (otpRefs.current[idx] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="w-12 h-14 text-center text-2xl font-bold border border-white/30 rounded-lg bg-white/10 backdrop-blur-sm text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all"
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    />
                  ))}
                </div>
                <div className="mt-2 text-center">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={countdown > 0 || loading}
                    className="text-sm text-white/60 hover:text-white disabled:cursor-not-allowed transition-colors"
                  >
                    {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className={btnClass}>
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify Email'
                )}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); setError(''); setMessage(''); }}
                  className="inline-flex items-center text-white/80 hover:text-white transition-colors"
                >
                  <FaArrowLeft className="mr-2" /> Change email
                </button>
              </div>
            </form>
          )}

          {/* ---- STEP 3: Details ---- */}
          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <p className="text-white/70 text-sm text-center">
                Verified: <span className="text-white font-medium">{email}</span>
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="sr-only">Full Name</label>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                    <input id="name" type="text" autoComplete="name" required className={inputClass} placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label htmlFor="companyName" className="sr-only">Company Name</label>
                  <div className="relative">
                    <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                    <input id="companyName" type="text" autoComplete="organization" required className={inputClass} placeholder="Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label htmlFor="phone" className="sr-only">Phone Number</label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                    <input id="phone" type="tel" autoComplete="tel" required className={inputClass} placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label htmlFor="position" className="sr-only">Job Position</label>
                  <div className="relative">
                    <FaBriefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                    <input id="position" type="text" autoComplete="organization-title" required className={inputClass} placeholder="Job Position" value={position} onChange={(e) => setPosition(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Honeypot */}
              <div aria-hidden="true" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0, overflow: 'hidden' }}>
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" autoComplete="off" tabIndex={-1} value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>

              {TURNSTILE_ENABLED && (
                <TurnstileWidget
                  key={turnstileResetKey}
                  enabled={TURNSTILE_ENABLED}
                  siteKey={TURNSTILE_SITE_KEY}
                  onVerify={(token) => { setError(''); setTurnstileToken(token); }}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => { setTurnstileToken(''); setError('Security verification failed. Please try again.'); }}
                />
              )}

              <button type="submit" disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)} className={btnClass}>
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting request...
                  </div>
                ) : (
                  'Submit Request'
                )}
              </button>

              <div className="text-center">
                <span className="text-white/60">Already have an account? </span>
                <Link to="/login" className="font-medium text-white hover:text-white/80 transition-colors">
                  Sign in here
                </Link>
              </div>
            </form>
          )}

          {/* ---- STEP 4: Success ---- */}
          {step === 4 && (
            <div className="text-center space-y-6">
              <div className="mx-auto w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <FaCheckCircle className="text-white text-3xl" />
              </div>
              <p className="text-white/80">
                Your license request has been submitted successfully. Our administrator will review your request and send you login credentials via email.
              </p>
              <Link
                to="/login"
                className="inline-block px-6 py-3 bg-white text-brand-purple font-medium rounded-lg hover:bg-white/90 transition-all duration-200"
              >
                Back to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LicenseRequest;
